import api from './api';
import {
  Client,
  ClientContact,
  ClientContactInput,
  ClientDocument,
  ClientDocumentInput,
  ClientNote,
  ClientNoteInput,
  ClientSummary,
  CreateClientInput,
  CreateClientFromSourceInput,
  ClientFilters,
  PaginationParams,
  ClientListResponse,
  ClientStatistics
} from '../types/client';

/**
 * Client API Service
 * Handles all API calls for client management
 * Used by both dispatch team (operations) and finance team (payments)
 */

export const clientApi = {
  // ==================== CRUD Operations ====================

  /**
   * Get all clients with filtering and pagination
   */
  async getAll(params?: ClientFilters & PaginationParams): Promise<ClientListResponse> {
    const response = await api.get('/clients', { params });
    return response.data;
  },

  /**
   * Get client by ID with all related data
   */
  async getById(id: number): Promise<Client> {
    const response = await api.get(`/clients/${id}`);
    return response.data;
  },

  /**
   * Get client by client code
   */
  async getByCode(code: string): Promise<Client> {
    const response = await api.get(`/clients/code/${code}`);
    return response.data;
  },

  /**
   * Create new third-party client
   */
  async create(data: CreateClientInput): Promise<Client> {
    const response = await api.post('/clients', data);
    return response.data;
  },

  /**
   * Create client from existing shipper
   */
  async createFromShipper(
    shipperId: number,
    overrides?: CreateClientFromSourceInput
  ): Promise<Client> {
    const response = await api.post(`/clients/from-shipper/${shipperId}`, overrides || {});
    return response.data;
  },

  /**
   * Create client from existing warehouse
   */
  async createFromWarehouse(
    warehouseId: number,
    overrides?: CreateClientFromSourceInput
  ): Promise<Client> {
    const response = await api.post(`/clients/from-warehouse/${warehouseId}`, overrides || {});
    return response.data;
  },

  /**
   * Update client
   */
  async update(id: number, data: Partial<CreateClientInput>): Promise<Client> {
    const response = await api.patch(`/clients/${id}`, data);
    return response.data;
  },

  /**
   * Delete client (soft delete)
   */
  async delete(id: number): Promise<Client> {
    const response = await api.delete(`/clients/${id}`);
    return response.data;
  },

  // ==================== Contact Management ====================

  /**
   * Add contact to client
   */
  async addContact(clientId: number, data: ClientContactInput): Promise<ClientContact> {
    const response = await api.post(`/clients/${clientId}/contacts`, data);
    return response.data;
  },

  /**
   * Update contact
   */
  async updateContact(contactId: number, data: Partial<ClientContactInput>): Promise<ClientContact> {
    const response = await api.patch(`/clients/contacts/${contactId}`, data);
    return response.data;
  },

  /**
   * Delete contact
   */
  async deleteContact(contactId: number): Promise<ClientContact> {
    const response = await api.delete(`/clients/contacts/${contactId}`);
    return response.data;
  },

  // ==================== Document Management ====================

  /**
   * Add document to client
   */
  async addDocument(clientId: number, data: ClientDocumentInput): Promise<ClientDocument> {
    const response = await api.post(`/clients/${clientId}/documents`, data);
    return response.data;
  },

  /**
   * Update document
   */
  async updateDocument(
    documentId: number,
    data: Partial<ClientDocumentInput>
  ): Promise<ClientDocument> {
    const response = await api.patch(`/clients/documents/${documentId}`, data);
    return response.data;
  },

  /**
   * Delete document
   */
  async deleteDocument(documentId: number): Promise<ClientDocument> {
    const response = await api.delete(`/clients/documents/${documentId}`);
    return response.data;
  },

  // ==================== Notes Management ====================

  /**
   * Add note to client
   */
  async addNote(clientId: number, data: ClientNoteInput): Promise<ClientNote> {
    const response = await api.post(`/clients/${clientId}/notes`, data);
    return response.data;
  },

  /**
   * Update note
   */
  async updateNote(noteId: number, data: Partial<ClientNoteInput>): Promise<ClientNote> {
    const response = await api.patch(`/clients/notes/${noteId}`, data);
    return response.data;
  },

  /**
   * Delete note
   */
  async deleteNote(noteId: number): Promise<ClientNote> {
    const response = await api.delete(`/clients/notes/${noteId}`);
    return response.data;
  },

  // ==================== Summary & Analytics ====================

  /**
   * Get client summary (cached statistics)
   */
  async getSummary(clientId: number): Promise<ClientSummary> {
    const response = await api.get(`/clients/${clientId}/summary`);
    return response.data;
  },

  /**
   * Manually refresh client summary
   */
  async refreshSummary(clientId: number): Promise<ClientSummary> {
    const response = await api.post(`/clients/${clientId}/summary/refresh`);
    return response.data;
  },

  /**
   * Get client statistics for dashboard
   */
  async getStatistics(): Promise<ClientStatistics> {
    const response = await api.get('/clients/statistics');
    return response.data;
  },

  /**
   * Get clients by source (shipper or warehouse)
   */
  async getBySource(sourceType: 'shipper' | 'warehouse', sourceId: number): Promise<Client[]> {
    const response = await api.get(`/clients/by-source/${sourceType}/${sourceId}`);
    return response.data;
  },

  /**
   * Real-time KPI aggregation: jobs, revenue, outstanding,
   * order/task status breakdown, on-time delivery rate
   */
  async getPaymentKpiSummary(
    clientId: number,
    periodStart?: string,
    periodEnd?: string
  ): Promise<ClientPaymentKpiSummary> {
    const params: Record<string, string> = {};
    if (periodStart) params.periodStart = periodStart;
    if (periodEnd) params.periodEnd = periodEnd;
    const response = await api.get(`/clients/${clientId}/kpi-summary`, { params });
    return response.data;
  },
};

export interface ClientPaymentKpiSummary {
  jobs: number;
  revenue: number;
  outstanding: number;
  orderBreakdown: {
    completed: number;
    inProgress: number;
    total: number;
  };
  taskBreakdown: {
    atPort: number;
    released: number;
    onHold: number;
    inDelivery: number;
    atYard: number;
  };
  onTimeRate: number | null;
  period: {
    from: string | null;
    to: string | null;
  };
}